"use client"

import type React from "react"

import { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  Grid,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  CircularProgress,
  IconButton,
  Collapse,
  Box,
  Typography,
} from "@mui/material"
import { useTranslation } from "../common/components/LocalizationProvider"
import { errorsActions, sessionActions } from "../store"
import PageLayout from "../common/components/PageLayout"
import ReportFilter from "../reports/components/ReportFilter"
import ColumnSelect from "../reports/components/ColumnSelect"
import TableShimmer from "../common/components/TableShimmer"
import useReportStyles from "../reports/common/useReportStyles"
import { useCatch } from "../reactHelper"
import { utils, writeFileXLSX } from "xlsx"
import LogoImage from "../login/LogoImage"
import { nativePostMessage } from "../common/components/NativeInterface"
import ReportsMenu from "./components/ReportsMenu"
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown"
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp"

interface LogItem {
  id: number
  attributes: Record<string, any>
  actionTime: string
  address: string
  userId: number
  actionType: string
  objectType: string | null
  objectId: number
}

interface User {
  id: number
  name: string
  email: string
}

const PrintHeader = ({ from, to, userIdFilter }) => {
  const t = useTranslation()
  const usersMap = useSelector((state) => state.usersMap || {})


  const printStyles = {
    container: {
      padding: 10,
      color: "#000",
    },
    header: {
      display: "flex",
      justifyContent: "space-between",
    },
    title: {
      fontSize: 24,
      margin: 0,
      color: "#000",
    },
    infoSection: {
      marginTop: 10,
      fontSize: 14,
      color: "#333",
    },
    infoLabel: {
      fontWeight: "bold",
      color: "#000",
    },
  }

  return (
    <div style={printStyles.container}>
      <div style={printStyles.header}>
        <LogoImage
          style={{ height: 60 }}
          onLoad={async () => {
            requestAnimationFrame(() =>
              requestAnimationFrame(() => {
                window.print()
                nativePostMessage(`print|${t("systemLogReport")}.pdf`)
              }),
            )
          }}
          alt={t("companyLogo")}
        />
        <h1 style={printStyles.title}>{t("systemLogReport") || "Logs de Ações"}</h1>
      </div>
      <div style={printStyles.infoSection}>
        {userIdFilter && (
          <div>
            <strong style={printStyles.infoLabel}>{t("shared.user")}:</strong>{" "}
            {usersMap[userIdFilter] || ` ${userIdFilter}` || "Todos os usuários"}
          </div>
        )}
        {from && (
          <div>
            <strong style={printStyles.infoLabel}>{t("reportFrom")}:</strong> {new Date(from).toLocaleString()}
          </div>
        )}
        {to && (
          <div>
            <strong style={printStyles.infoLabel}>{t("reportTo")}:</strong> {new Date(to).toLocaleString()}
          </div>
        )}
      </div>
    </div>
  )
}

const AttributesRenderer = ({ attributes }) => {
  const [open, setOpen] = useState(false)

  if (!attributes || Object.keys(attributes).length === 0) {
    return <span>-</span>
  }

  const formattedAttributes = {}

  Object.entries(attributes).forEach(([key, value]) => {
    if (typeof value === "string" && (value.startsWith("[") || value.startsWith("{"))) {
      try {
        const parsed = JSON.parse(value)
        formattedAttributes[key] = parsed
      } catch (e) {
        formattedAttributes[key] = value
      }
    } else {
      formattedAttributes[key] = value
    }
  })

  const preStyle = {
    margin: 0,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    fontSize: "0.75rem",
    fontFamily: "monospace",
    backgroundColor: "rgba(0, 0, 0, 0.04)",
    padding: "8px",
    borderRadius: "4px",
    maxHeight: "150px",
    overflow: "auto",
  }


  const attributeCount = Object.keys(formattedAttributes).length

  return (
    <div>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <IconButton
          aria-label={open ? "Minimizar atributos" : "Expandir atributos"}
          size="small"
          onClick={() => setOpen(!open)}
        >
          {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        </IconButton>
        <Typography variant="body2" sx={{ ml: 1 }}>
          {open ? "Minimizar" : `${attributeCount} atributo${attributeCount !== 1 ? "s" : ""}`}
        </Typography>
      </Box>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <pre style={preStyle}>{JSON.stringify(formattedAttributes, null, 2)}</pre>
      </Collapse>
    </div>
  )
}

const UserNameFilter = ({ onChange, users = [] }) => {
  const [userId, setUserId] = useState("")
  const t = useTranslation()
  const usersMap = useSelector((state) => state.usersMap || {})

  const handleChange = (e) => {
    setUserId(e.target.value)
    onChange(e.target.value)
  }

  const getUserDisplayName = (user) => {
    const nameFromMap = usersMap[user.id]

    if (nameFromMap) {
      return nameFromMap
    }

    if (user.name) {
      return user.name
    }

    return `${user.id}`
  }

  return (
    <FormControl fullWidth variant="outlined">
      <InputLabel id="usuario-select-label">{t("shared.user")}</InputLabel>
      <Select
        labelId="usuario-select-label"
        value={userId}
        onChange={handleChange}
        label={t("shared.user")}
        displayEmpty
      >
        <MenuItem value="">
          <em>Todos os usuários</em>
        </MenuItem>
        {users.map((user) => (
          <MenuItem key={user.id} value={user.id.toString()}>
            {getUserDisplayName(user)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  )
}

const ActionsReportPage = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const classes = useReportStyles()
  const t = useTranslation()
  const [logs, setLogs] = useState<LogItem[]>([])
  const [todosLogs, setTodosLogs] = useState<LogItem[]>([])
  const [loading, setLoading] = useState(false)
  const [usersLoading, setUsersLoading] = useState(false)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [userIdFilter, setUserIdFilter] = useState("")
  const [periodoFilter, setPeriodoFilter] = useState({ from: "", to: "" })
  const [usuarios, setUsuarios] = useState([])
  const [from, setFrom] = useState("")
  const [to, setTo] = useState("")
  const printing = useSelector((state) => state.session.printing)
  const usersMap = useSelector((state) => state.usersMap || {})

  const [available, setAvailable] = useState([])
  const [columns, setColumns] = useState([
    "id",
    "userId",
    "actionType",
    "objectType",
    "address",
    "actionTime",
    "attributes",
  ])

  useEffect(() => {
    const handleAfterPrint = () => {
      dispatch(sessionActions.setPrinting(false))
    }
    window.addEventListener("afterprint", handleAfterPrint)
    return () => {
      window.removeEventListener("afterprint", handleAfterPrint)
    }
  }, [])

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setUsersLoading(true)
        const response = await fetch("/api/users")
        if (response.ok) {
          const data = await response.json()

          const userNamesMap = {}
          data.forEach((user) => {
            userNamesMap[user.id] = user.name
          })

          dispatch({
            type: "usersMap/update",
            payload: userNamesMap,
          })

          // console.log("Mapa de usuários carregado:", userNamesMap)

          setUsuarios(data)
        } else {
          console.error("Falha ao buscar usuários:", await response.text())
        }
      } catch (error) {
        console.error("Erro ao buscar usuários:", error)
      } finally {
        setUsersLoading(false)
      }
    }

    fetchUsers()
  }, [dispatch])

  const buscarLogs = async (filtros: Record<string, any> = {}) => {
    setLoading(true)
    try {
      const query = new URLSearchParams()
      Object.entries(filtros).forEach(([key, value]) => {
        if (value) query.append(key, value.toString())
      })

      const endpoint = query.toString() ? `/api/actions?${query.toString()}` : "/api/actions"
      const resposta = await fetch(endpoint)

      if (resposta.ok) {
        const dados = await resposta.json()
        setTodosLogs(dados)

        if (Object.keys(filtros).length === 0) {
          const dadosFiltrados = aplicarFiltrosLocais(dados, userIdFilter, periodoFilter)
          setLogs(dadosFiltrados)
        } else {
          setLogs(dados)
        }

        setPage(0)

        if (dados.length > 0) {
          const keySet = new Set()
          dados.forEach((log: LogItem) => {
            Object.keys(log).forEach((key) => keySet.add(key))
          })

          setAvailable(
            [...keySet]
              .map((key) => [key, getColumnDisplayName(key as string)])
              .sort((a, b) => a[1].localeCompare(b[1])),
          )
        }
      } else {
        throw Error(await resposta.text())
      }
    } catch (erro) {
      if (erro instanceof Error) {
        dispatch(errorsActions.push(erro.message))
      }
    } finally {
      setLoading(false)
    }
  }

  const aplicarFiltrosLocais = (logs, userId, periodo) => {
    let resultado = [...logs]

    if (userId) {
      resultado = resultado.filter((log) => log.userId.toString() === userId)
    }

    if (periodo.from || periodo.to) {
      resultado = resultado.filter((log) => {
        const dataLog = new Date(log.actionTime)

        if (periodo.from && periodo.to) {
          const dataInicio = new Date(periodo.from)
          const dataFim = new Date(periodo.to)
          dataFim.setHours(23, 59, 59, 999)

          return dataLog >= dataInicio && dataLog <= dataFim
        } else if (periodo.from) {
          const dataInicio = new Date(periodo.from)
          return dataLog >= dataInicio
        } else if (periodo.to) {
          const dataFim = new Date(periodo.to)
          dataFim.setHours(23, 59, 59, 999)
          return dataLog <= dataFim
        }

        return true
      })
    }

    return resultado
  }

  const filtrarPorUserId = (userId) => {
    setUserIdFilter(userId)
    const dadosFiltrados = aplicarFiltrosLocais(todosLogs, userId, periodoFilter)
    setLogs(dadosFiltrados)
    setPage(0)
  }

  const filtrarPorPeriodo = (from, to) => {
    setPeriodoFilter({ from, to })
  }

  const getColumnDisplayName = (column: string): string => {
    switch (column) {
      case "id":
        return "ID"
      case "userId":
        return "Usuário"
      case "actionType":
        return "Tipo de Ação"
      case "objectType":
        return "Tipo de Objeto"
      case "objectId":
        return "ID do Objeto"
      case "address":
        return "Endereço IP"
      case "actionTime":
        return "Data e Hora"
      case "attributes":
        return "Atributos"
      default:
        return column
    }
  }

  useEffect(() => {
    buscarLogs()
  }, [])

  const handleChangePage = (event: unknown, novaPagina: number) => {
    setPage(novaPagina)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10))
    setPage(0)
  }

  const formatarDataHora = (stringDataHora: string): string => {
    const data = new Date(stringDataHora)
    return data.toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" })
  }

  const formatAttributesForPrinting = (attributes) => {
    if (!attributes || Object.keys(attributes).length === 0) return "-"

    const formattedPairs = Object.entries(attributes).map(([key, value]) => {
      let displayValue = value

      if (typeof value === "string" && (value.startsWith("[") || value.startsWith("{"))) {
        try {
          const parsed = JSON.parse(value)
          if (Array.isArray(parsed)) {
            displayValue = `[${parsed.join(", ")}]`
          } else {
            displayValue = JSON.stringify(parsed, null, 2)
          }
        } catch (e) {
          displayValue = value
        }
      }

      return `${key}: ${displayValue}`
    })

    return formattedPairs.join("\n")
  }

  const renderCellValue = (log: LogItem, column: string) => {
    switch (column) {
      case "id":
        return log.id
      case "userId":
        return usersMap[log.userId] || `${log.userId}`
      case "actionType":
        return log.actionType
      case "objectType":
        return log.objectType || "-"
      case "objectId":
        return log.objectId || "-"
      case "address":
        return log.address
      case "actionTime":
        return formatarDataHora(log.actionTime)
      case "attributes":
        if (printing) {
          return formatAttributesForPrinting(log.attributes)
        }

        return <AttributesRenderer attributes={log.attributes} />
      default:
        return log[column] !== undefined ? log[column] : "-"
    }
  }

  const processarPeriodoPredefinido = (periodo) => {

    const hoje = new Date()
    const dataInicio = new Date()
    const dataFim = new Date()

    const hojeBrasilia = new Date(hoje.toLocaleString("en-US", { timeZone: "America/Sao_Paulo" }))
    const dataInicioBrasilia = new Date(dataInicio.toLocaleString("en-US", { timeZone: "America/Sao_Paulo" }))
    const dataFimBrasilia = new Date(dataFim.toLocaleString("en-US", { timeZone: "America/Sao_Paulo" }))

    switch (periodo) {
      case "hoje":
        dataInicioBrasilia.setHours(0, 0, 0, 0)

        return { from: dataInicioBrasilia.toISOString(), to: dataFimBrasilia.toISOString() }

      case "ontem":
        dataInicioBrasilia.setDate(dataInicioBrasilia.getDate() - 1)
        dataInicioBrasilia.setHours(0, 0, 0, 0)
        dataFimBrasilia.setDate(dataFimBrasilia.getDate() - 1)
        dataFimBrasilia.setHours(23, 59, 59, 999)
        return { from: dataInicioBrasilia.toISOString(), to: dataFimBrasilia.toISOString() }

      case "esta_semana":
        const diaAtual = hojeBrasilia.getDay()
        const diasParaSubtrair = diaAtual === 0 ? 6 : diaAtual - 1
        dataInicioBrasilia.setDate(dataInicioBrasilia.getDate() - diasParaSubtrair)
        dataInicioBrasilia.setHours(0, 0, 0, 0)
        return { from: dataInicioBrasilia.toISOString(), to: dataFimBrasilia.toISOString() }

      case "semana_passada":
        const diaHoje = hojeBrasilia.getDay()
        const diasParaInicioSemanaPassada = diaHoje === 0 ? 13 : diaHoje + 6
        dataInicioBrasilia.setDate(dataInicioBrasilia.getDate() - diasParaInicioSemanaPassada)
        dataInicioBrasilia.setHours(0, 0, 0, 0)

        dataFimBrasilia.setDate(dataFimBrasilia.getDate() - (diaHoje === 0 ? 7 : diaHoje))
        dataFimBrasilia.setHours(23, 59, 59, 999)
        return { from: dataInicioBrasilia.toISOString(), to: dataFimBrasilia.toISOString() }

      case "este_mes":
        dataInicioBrasilia.setDate(1)
        dataInicioBrasilia.setHours(0, 0, 0, 0)
        return { from: dataInicioBrasilia.toISOString(), to: dataFimBrasilia.toISOString() }

      case "mes_passado":
        dataInicioBrasilia.setMonth(dataInicioBrasilia.getMonth() - 1)
        dataInicioBrasilia.setDate(1)
        dataInicioBrasilia.setHours(0, 0, 0, 0)

        dataFimBrasilia.setDate(0)
        dataFimBrasilia.setHours(23, 59, 59, 999)
        return { from: dataInicioBrasilia.toISOString(), to: dataFimBrasilia.toISOString() }

      default:
        return { from: "", to: "" }
    }
  }

  const handleSubmit = useCatch(async ({ from, to, type, userId, period, ...otherFilters }) => {
    if (type === "mail") {
      const response = await fetch(`/api/actions/mail`)
      if (!response.ok) {
        throw Error(await response.text())
      }
    } else {
      let dataInicio = from
      let dataFim = to

      if (period && period !== "custom") {
        const periodoProcessado = processarPeriodoPredefinido(period)
        dataInicio = periodoProcessado.from
        dataFim = periodoProcessado.to
      }


      setPeriodoFilter({ from: dataInicio, to: dataFim })

      const filtros: Record<string, any> = { ...otherFilters }
      if (dataInicio) filtros.from = dataInicio
      if (dataFim) filtros.to = dataFim
      if (userId) filtros.userId = userId


      await buscarLogs(filtros)

      const dadosFiltrados = aplicarFiltrosLocais(todosLogs, userIdFilter, { from: dataInicio, to: dataFim })
      setLogs(dadosFiltrados)

      if (type === "export") {
        const table = document.querySelector("table")
        requestAnimationFrame(() => writeFileXLSX(utils.table_to_book(table), "logs.xlsx"))
      } else if (type === "pdf") {
        setFrom(dataInicio)
        setTo(dataFim)
        dispatch(sessionActions.setPrinting(true))
      }
    }
  })

  const handleSchedule = useCatch(async (userIds, groupIds, report) => {
    report.type = "logs"
    navigate("/reports/scheduled")
  })


  useEffect(() => {
    if (printing) {
      const styleElement = document.createElement("style")
      styleElement.id = "pdf-print-styles"
      styleElement.innerHTML = `
      @media print {
        body, table, th, td, h1, h2, h3, h4, h5, h6, p, span, div {
          color: black !important;
          background-color: white !important;
          border-color: #ddd !important;
        }

        table {
          width: 100%;
          border-collapse: collapse;
        }

        th, td {
          border: 1px solid #ddd !important;
          padding: 8px !important;
          text-align: left !important;
        }

        th {
          background-color: #f2f2f2 !important;
          font-weight: bold !important;
        }

        pre {
          white-space: pre-wrap !important;
          word-break: break-word !important;
          background-color: #f5f5f5 !important;
          color: black !important;
          border: 1px solid #ddd !important;
          padding: 8px !important;
        }
      }
    `
      document.head.appendChild(styleElement)

      return () => {
        const element = document.getElementById("pdf-print-styles")
        if (element) {
          element.remove()
        }
      }
    }
  }, [printing])

  if (printing) {
    return (
      <div style={{ color: "black", backgroundColor: "white" }}>
        <PrintHeader from={from} to={to} userIdFilter={userIdFilter} />
        <div>
          <Table style={{ width: "100%", borderCollapse: "collapse" }}>
            <TableHead>
              <TableRow>
                {columns.map((key) => (
                  <TableCell
                    key={key}
                    style={{ color: "black", backgroundColor: "#f2f2f2", fontWeight: "bold", border: "1px solid #ddd" }}
                  >
                    {getColumnDisplayName(key)}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.map((log) => (
                <TableRow key={log.id}>
                  {columns.map((column) => (
                    <TableCell key={`${log.id}-${column}`} style={{ color: "black", border: "1px solid #ddd" }}>
                      {renderCellValue(log, column)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    )
  }

  return (
    <PageLayout menu={<ReportsMenu />} breadcrumbs={["Sistema", "Logs de Ações"]}>
      <div className={classes.container}>
        <div className={classes.containerMain}>
          <div className={classes.header}>
            <ReportFilter
              handleSubmit={handleSubmit}
              handleSchedule={handleSchedule}
              loading={loading}
              ignoreDevice={true}
              showOnly={null}
              includeExportOptions={true}
            >
              <Grid item xs={12} sm={6}>
                {usersLoading ? (
                  <CircularProgress size={24} />
                ) : (
                  <UserNameFilter onChange={filtrarPorUserId} users={usuarios} />
                )}
              </Grid>
              <Grid item xs={12} sm={12}>
                <ColumnSelect
                  columns={columns}
                  setColumns={setColumns}
                  columnsArray={available}
                  rawValues
                  disabled={!logs.length}
                />
              </Grid>
            </ReportFilter>
          </div>

          <Table>
            <TableHead>
              <TableRow>
                {columns.map((key) => (
                  <TableCell key={key}>{getColumnDisplayName(key)}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading ? (
                logs.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((log) => (
                  <TableRow key={log.id} hover>
                    {columns.map((column) => (
                      <TableCell key={`${log.id}-${column}`}>{renderCellValue(log, column)}</TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableShimmer columns={columns.length} />
              )}
            </TableBody>
          </Table>

          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={logs.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Linhas por página:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          />
        </div>
      </div>
    </PageLayout>
  )
}

export default ActionsReportPage